// Level tier definitions for XP-based progression.
//
// Levels 1-10 (0 - 7,000) are the original "classic" animal ladder and must never
// change — students already sit on them and any shift would demote them.
//
// Above 7,000 the ladder continues with high tiers every 2,000 points from 10,000
// to 50,000: gold "mythic" beasts up to 28,000, then red "legendary" beasts from
// 30,000 to 50,000. `variant` marks a level as specially styled — the components
// swap their inline colour gradient for a CSS class of the same name.
//
// Spacing is set against the real earning rate: under the derived scoring model
// (backend/features/gamification/points) an active student earns roughly 2,800
// points a month, so a 2,000-point tier is about three weeks of work.
const CLASSIC_LEVELS = [
  {
    level: 1,
    name: '小书虫',
    nameEn: 'Bookworm',
    minPoints: 0,
    color: '#c9a87c',     // Bronze
    colorLight: '#dcc4a8',
    colorDark: '#a8896c',
    icon: '🐛',
  },
  {
    level: 2,
    name: '跳跳蛙',
    nameEn: 'Frog',
    minPoints: 250,
    color: '#87a878',     // Sage green
    colorLight: '#a8c99e',
    colorDark: '#6b8a5e',
    icon: '🐸',
  },
  {
    level: 3,
    name: '小刺猬',
    nameEn: 'Hedgehog',
    minPoints: 500,
    color: '#7eb8c9',     // Sky blue
    colorLight: '#a4d4e4',
    colorDark: '#5a9aae',
    icon: '🦔',
  },
  {
    level: 4,
    name: '智慧猫头鹰',
    nameEn: 'Owl',
    minPoints: 1000,
    color: '#9580b8',     // Purple
    colorLight: '#b8a0d0',
    colorDark: '#7a68a0',
    icon: '🦉',
  },
  {
    level: 5,
    name: '勇敢狮子',
    nameEn: 'Lion',
    minPoints: 2000,
    color: '#e07a5f',     // Coral
    colorLight: '#f4a89a',
    colorDark: '#c65d42',
    icon: '🦁',
  },
  {
    level: 6,
    name: '智慧龙骑士',
    nameEn: 'Dragon',
    minPoints: 3000,
    color: '#f2a541',     // Gold
    colorLight: '#fcd89d',
    colorDark: '#d4892a',
    icon: '🐉',
  },
  {
    level: 7,
    name: '火烈鸟',
    nameEn: 'Flamingo',
    minPoints: 4000,
    color: '#e85d04',     // Orange-red
    colorLight: '#ff9e6d',
    colorDark: '#c44d00',
    icon: '🦩',
  },
  {
    level: 8,
    name: '独角兽骑士',
    nameEn: 'Unicorn',
    minPoints: 5000,
    color: '#e091c9',     // Pink
    colorLight: '#f4b8e0',
    colorDark: '#c46aa8',
    icon: '🦄',
  },
  {
    level: 9,
    name: '八爪大师',
    nameEn: 'Octopus',
    minPoints: 6000,
    color: '#6b5b95',     // Deep purple
    colorLight: '#9a8cc2',
    colorDark: '#4a3d6e',
    icon: '🐙',
  },
  {
    level: 10,
    name: '恐龙王',
    nameEn: 'T-Rex',
    minPoints: 7000,
    color: '#f2a541',
    colorLight: '#fff9e6',
    colorDark: '#d4892a',
    icon: '🦖',
    variant: 'rainbow',   // Special animated rainbow gradient
  },
];

// High tiers, in order, one per 2,000 points from 10,000 to 50,000.
const HIGH_TIER_BEASTS = [
  // Mythic (gold) — 10,000 to 28,000
  ['九尾狐', 'Nine-Tailed Fox', '🦊'],
  ['白虎', 'White Tiger', '🐅'],
  ['玄武', 'Black Tortoise', '🐢'],
  ['朱雀', 'Vermilion Bird', '🦅'],
  ['麒麟', 'Qilin', '🦌'],
  ['凤凰', 'Phoenix', '🦚'],
  ['鲲鹏', 'Kunpeng', '🐋'],
  ['貔貅', 'Pixiu', '🐆'],
  ['饕餮', 'Taotie', '🐗'],
  ['白泽', 'Baize', '🦬'],
  // Legendary (red) — 30,000 to 50,000
  ['青龙', 'Azure Dragon', '🐲'],
  ['天马', 'Pegasus', '🐎'],
  ['巨齿鲨王', 'Megalodon', '🦈'],
  ['冰霜狼王', 'Frost Wolf', '🐺'],
  ['深海巨蟒', 'Sea Serpent', '🐍'],
  ['暗夜蝠王', 'Night Bat King', '🦇'],
  ['远古猛犸', 'Mammoth', '🦣'],
  ['熔岩巨鳄', 'Lava Crocodile', '🐊'],
  ['雷霆犀王', 'Thunder Rhino', '🦏'],
  ['天穹圣象', 'Celestial Elephant', '🐘'],
  ['星河神兽', 'Cosmic Beast', '🌌'],
];

export const HIGH_TIER_START = 10000;
export const HIGH_TIER_STEP = 2000;
export const LEGENDARY_START = 30000;

const MYTHIC_PALETTE = { color: '#f0b429', colorLight: '#ffe08a', colorDark: '#b8860b' };
const LEGENDARY_PALETTE = { color: '#e03131', colorLight: '#ff8787', colorDark: '#9a1414' };

const HIGH_TIER_LEVELS = HIGH_TIER_BEASTS.map(([name, nameEn, icon], i) => {
  const minPoints = HIGH_TIER_START + i * HIGH_TIER_STEP;
  const isLegendary = minPoints >= LEGENDARY_START;

  return {
    level: CLASSIC_LEVELS.length + i + 1,
    name,
    nameEn,
    minPoints,
    ...(isLegendary ? LEGENDARY_PALETTE : MYTHIC_PALETTE),
    icon,
    variant: isLegendary ? 'legendary' : 'mythic',
  };
});

export const LEVELS = [...CLASSIC_LEVELS, ...HIGH_TIER_LEVELS];

export function getLevelForPoints(points) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (points >= LEVELS[i].minPoints) {
      return LEVELS[i];
    }
  }
  return LEVELS[0];
}

/**
 * Levels a student is allowed to see.
 *
 * Below T-Rex the full classic ladder is visible — the ten animals are the
 * roadmap, and seeing 恐龙王 waiting at the top is the point. Once T-Rex is
 * unlocked the high tiers reveal one at a time, so the long climb to 100,000
 * never spoils itself.
 *
 * @param {number} points
 * @returns {typeof LEVELS}
 */
export function getVisibleLevels(points) {
  const currentLevel = getLevelForPoints(points);
  const currentLevelIndex = LEVELS.findIndex(l => l.level === currentLevel.level);

  if (currentLevelIndex < CLASSIC_LEVELS.length - 1) {
    return LEVELS.slice(0, CLASSIC_LEVELS.length);
  }

  return LEVELS.slice(0, currentLevelIndex + 2);
}

export function getProgressToNextLevel(points) {
  const currentLevel = getLevelForPoints(points);
  const currentLevelIndex = LEVELS.findIndex(l => l.level === currentLevel.level);

  if (currentLevelIndex === LEVELS.length - 1) {
    return {
      progress: 100,
      currentPoints: points,
      nextLevelPoints: currentLevel.minPoints,
      pointsToNext: 0,
      isMaxLevel: true,
    };
  }

  const nextLevel = LEVELS[currentLevelIndex + 1];
  const pointsInCurrentLevel = points - currentLevel.minPoints;
  const pointsNeededForNext = nextLevel.minPoints - currentLevel.minPoints;
  const progress = Math.min(100, (pointsInCurrentLevel / pointsNeededForNext) * 100);

  return {
    progress,
    currentPoints: points,
    nextLevelPoints: nextLevel.minPoints,
    pointsToNext: nextLevel.minPoints - points,
    isMaxLevel: false,
  };
}

export function checkLevelUp(oldPoints, newPoints) {
  const oldLevel = getLevelForPoints(oldPoints);
  const newLevel = getLevelForPoints(newPoints);

  if (newLevel.level > oldLevel.level) {
    return { previousLevel: oldLevel, newLevel };
  }

  return null;
}

export function getNextLevel(points) {
  const currentLevel = getLevelForPoints(points);
  const currentLevelIndex = LEVELS.findIndex(l => l.level === currentLevel.level);

  if (currentLevelIndex === LEVELS.length - 1) {
    return null;
  }

  return LEVELS[currentLevelIndex + 1];
}

// beiski — Rob's admin test account (prod user 3). Its level animal shows the ladder as if at
// the top — on the dashboard and in kids songs alike — so every level animal can be checked on
// a real phone. Display only: its real 积分, homework and reports are untouched.
const LADDER_PREVIEW_STUDENT_ID = '6895d695d2449d197e357558';

/** The points a student's level animal is drawn from (beiski sees the top). */
export function ladderPointsFor(studentId, points) {
  return studentId === LADDER_PREVIEW_STUDENT_ID ? LEVELS[LEVELS.length - 1].minPoints : (points || 0);
}
